// src/components/InvitesTable.jsx
// Table showing pending invitations with status badges and actions

import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import { useInvites, getInviteStatus } from "../hooks/useInvites";

/**
 * InvitesTable - Table showing all invitations with actions
 *
 * Features:
 * - Status badges (Active, Expired, Used, Inactive)
 * - Copy code to clipboard
 * - Regenerate expired codes
 * - Deactivate invites
 */
export default function InvitesTable() {
  const theme = useTheme();
  const {
    invites,
    loading,
    error,
    refetch,
    deleteInvite,
    regenerateInvite,
    deleting,
    regenerating,
  } = useInvites();

  const [copiedId, setCopiedId] = useState(null);

  const handleCopyCode = async (invite) => {
    try {
      await navigator.clipboard.writeText(invite.code);
      setCopiedId(invite.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleRegenerate = async (inviteId) => {
    try {
      const newInvite = await regenerateInvite(inviteId);
      // Automatically copy the new code
      await navigator.clipboard.writeText(newInvite.code);
      setCopiedId(inviteId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to regenerate:", err);
    }
  };

  const handleDelete = async (inviteId) => {
    if (!window.confirm("Are you sure you want to deactivate this invite code?")) {
      return;
    }
    try {
      await deleteInvite(inviteId);
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStatusChip = (invite) => {
    const { status, color } = getInviteStatus(invite);
    return (
      <Chip
        label={status}
        size="small"
        color={color}
        variant={color === "default" ? "outlined" : "filled"}
        sx={{ fontWeight: 600, fontSize: "0.7rem" }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No invitation codes found. Create one to invite team members.
        </Typography>
      </Box>
    );
  }

  if (!invites || invites.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No invitation codes yet. Create one to invite team members.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Code</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="center">Uses</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Expires</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invites.map((invite) => {
            const { canRegenerate } = getInviteStatus(invite);
            const isDeleting = deleting === invite.id;
            const isRegenerating = regenerating === invite.id;
            const isCopied = copiedId === invite.id;

            return (
              <TableRow
                key={invite.id}
                sx={{
                  opacity: invite.is_active ? 1 : 0.6,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.action.hover, 0.5),
                  },
                }}
              >
                {/* Code */}
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 600,
                        letterSpacing: "0.05em",
                      }}
                    >
                      {invite.code}
                    </Typography>
                    <Tooltip title={isCopied ? "Copied!" : "Copy code"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopyCode(invite)}
                        sx={{
                          color: isCopied ? "success.main" : "text.secondary",
                        }}
                      >
                        {isCopied ? (
                          <CheckRoundedIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>

                {/* Email */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {invite.email || (
                      <span style={{ fontStyle: "italic" }}>Anyone</span>
                    )}
                  </Typography>
                </TableCell>

                {/* Role */}
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {invite.role}
                  </Typography>
                </TableCell>

                {/* Uses */}
                <TableCell align="center">
                  <Typography variant="body2">
                    {invite.uses} / {invite.max_uses}
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell>{getStatusChip(invite)}</TableCell>

                {/* Expires */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(invite.expires_at)}
                  </Typography>
                </TableCell>

                {/* Actions */}
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {canRegenerate && invite.is_active && (
                      <Tooltip title="Regenerate code">
                        <IconButton
                          size="small"
                          onClick={() => handleRegenerate(invite.id)}
                          disabled={isRegenerating}
                          sx={{
                            color: "primary.main",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                          }}
                        >
                          {isRegenerating ? (
                            <CircularProgress size={16} />
                          ) : (
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}

                    {invite.is_active && (
                      <Tooltip title="Deactivate">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(invite.id)}
                          disabled={isDeleting}
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              bgcolor: alpha(theme.palette.error.main, 0.1),
                              color: "error.main",
                            },
                          }}
                        >
                          {isDeleting ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
